import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { authService } from '@/services/authService';
import { isActionableFailure } from '@/lib/authFailures';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { AuthIntro } from '@/components/auth/AuthIntro';
import { AuthCard } from '@/components/auth/AuthCard';
import { AuthField } from '@/components/auth/AuthField';
import { AuthAlert } from '@/components/auth/AuthAlert';
import { AuthLink } from '@/components/auth/AuthLink';
import { AuthButtonLink } from '@/components/auth/AuthButtonLink';
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton';

const schema = yup.object({
  email: yup.string().email('Please enter a valid email').required('Email is required'),
});

type ForgotPasswordFormData = {
  email: string;
};

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(schema),
  });

  const requestReset = async (email: string) => {
    try {
      setLoading(true);
      setError(null);
      await authService.requestPasswordReset(email);
      setSuccess(true);
    } catch (err: unknown) {
      // AL-1780: only failures the user can act on are shown. Anything else —
      // including an address with no account — goes on to the confirmation, so
      // the screen looks the same either way and nobody can tell them apart.
      if (!isActionableFailure(err)) {
        setSuccess(true);

        return;
      }

      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(
        status === 429
          ? t('app.auth.too_many_attempts')
          : t('app.auth.recovery_failed')
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (data: ForgotPasswordFormData) => requestReset(data.email);

  // AL-1792: "Resend email" is new in this layout. It repeats the request the
  // user already made, so it goes through the same call with the same address.
  const handleResend = () => requestReset(getValues('email'));

  if (success) {
    return (
      <AuthLayout>
        <AuthIntro
          badge={t('app.auth.reset_badge')}
          title={t('app.auth.sent_title')}
          description={
            <>
              {t('app.auth.sent_instructions_to', { email: getValues('email') })}
              <br />
              {t('app.auth.sent_open_inbox')}
            </>
          }
        />

        <AuthCard>
          <div className="flex flex-col gap-4">
            {error ? <AuthAlert>{error}</AuthAlert> : null}

            <p className="text-sm text-auth-body">{t('app.auth.check_spam')}</p>

            <AuthButtonLink to="/login">{t('app.auth.back_to_sign_in')}</AuthButtonLink>

            <p className="text-center text-xs text-auth-body">
              {t('app.auth.didnt_receive')}{' '}
              <button
                type="button"
                onClick={handleResend}
                disabled={loading}
                className="whitespace-nowrap font-semibold text-auth-accent hover:underline disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? t('app.auth.sending') : t('app.auth.resend_email')}
              </button>
            </p>
          </div>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthIntro
        badge={t('app.auth.reset_badge')}
        title={t('app.auth.forgot_title')}
        description={t('app.auth.forgot_subtitle')}
      />

      <AuthCard>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          {error ? <AuthAlert>{error}</AuthAlert> : null}

          <AuthField
            {...register('email')}
            label={t('app.auth.email')}
            labelHidden
            type="email"
            autoComplete="email"
            placeholder={t('app.auth.email_placeholder')}
            error={errors.email?.message}
          />

          <AuthSubmitButton loading={loading} loadingLabel={t('app.auth.sending')}>
            {t('app.auth.send_reset_link')}
          </AuthSubmitButton>

          <p className="text-center text-sm text-auth-body">
            {t('app.auth.remember_it')} <AuthLink to="/login">{t('app.auth.sign_in')}</AuthLink>
          </p>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
